<template>
  <div
    v-if="caseStudy"
    class="grossingSample"
  >
    <vt-side-panel
      v-if="toggleSidepanel && caseStudy.requestLabel"
      :visible="toggleSidepanel"
    >
      <study-request-detail
        :cas-id="casId"
        :lab-id="laboratoryIdLogged"
      >
        <b-button
          id="requestsSidePanelClose"
          slot="buttons-end"
          v-shortkey="['shift', 'esc']"
          class="btn__icon btn__icon--primary pl-3 pr-0 py-0"
          @shortkey.prevent="closeRequestSidePanel()"
          @click="closeRequestSidePanel()"
        >
          <i
            class="fal fa-times"
            aria-hidden="true"
          />
        </b-button>
      </study-request-detail>
    </vt-side-panel>

    <!--Grossing Header-->
    <b-row class="search-block mb-2 mt-1">
      <b-col
        xl="4"
        cols="6"
      >
        <searcher-module
          ref="searcherModule"
          name="grossingSearcher"
          :error-message="$tc('grossing.error.validIdentifier')"
          portion-search
          sample-search
          case-search
          @portionSearch="onPortionSearch($event)"
          @sampleSearch="onSampleSearch($event)"
          @studySearch="onStudySearch($event)"
        />
      </b-col>
      <b-col
        xl="8"
        cols="6"
        class="d-flex align-items-center justify-content-end"
      >
        <div>
          <b-button
            v-shortkey="['f10']"
            qa="sample-processing-view-patients-button"
            class="btn__link px-3"
            @shortkey.prevent="openPatientModal(false)"
            @click="openPatientModal(false)"
          >
            <i
              class="far fa-users mr-2"
              aria-hidden="true"
            />{{ $tc('global.patients') }}
          </b-button>
          <b-button
            v-tooltip="$tc('global.statusOptions.incidence',2)"
            qa="sample-processing-view-incidents-button"
            class="round-btn round-btn--incidents mr-0"
            @click="openIncidentModal($event)"
          >
            <i
              class="fal fa-exclamation-triangle"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-tooltip="$tc('global.file',2)"
            qa="sample-processing-view-attachments-button"
            class="round-btn round-btn--attachments ml-2 mr-1"
            @click="showPatientAttachmentTreeModal"
          >
            <i
              class="fal fa-paperclip"
              aria-hidden="true"
            />
          </b-button>
        </div>
      </b-col>
    </b-row>

    <div class="mainLayout">
      <b-form-row class="mb-2">
        <b-col class="col-md-6 col-12 pb-2 pb-md-0">
          <div class="entity-card">
            <b-row class="py-2 px-3">
              <b-col class="d-flex align-items-center">
                <b-button
                  class="btn__icon btn__icon--primary"
                  @click="showCaseModal"
                >
                  <i
                    class="far fa-chart-network mr-2 main"
                    aria-hidden="true"
                  />
                </b-button>
                <span
                  class="entity-card__title d-flex align-items-center font-weight--medium"
                  qa="sample-processing-view-case-label"
                >
                  {{ caseStudy.casenumber }}
                  <i
                    v-if="visibleHasUnauthorizedBillableConceptsIcon(caseStudy.hasUnauthorizedBillableConcepts)"
                    v-tooltip="$t('global.hasUnauthorizedBillableConcepts')"
                    aria-hidden="true"
                    class="ml-1 mr-1 fas fa-calculator danger-color"
                  />
                </span>
                <b-badge
                  v-if="caseStudy.caseOfReference"
                  v-tooltip="$tc('registry.btn.consultation')"
                  pill
                  class="ml-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
                >
                  <i
                    class="far fa-glasses"
                    aria-hidden="true"
                  />
                </b-badge>
                <b-badge
                  v-if="caseStudy.lastAmendmentDate"
                  v-tooltip="$tc('global.amendedCase')"
                  pill
                  class="icon-badge indicator-bubble d-flex align-items-center justify-content-center"
                >
                  <i
                    class="far fa-money-check-edit"
                    aria-hidden="true"
                  />
                </b-badge>
                <!-- TODO: Report read by healthcare professional indicator, uncomment when developed -->
                <!--<b-badge @click="$refs.commonPetitionerListModalDetailWrapper.show()" v-tooltip="$tc('global.reportReadByHealthcareProfessional')" pill class="ml-1 is-cursor-pointer icon-badge indicator-bubble d-flex align-items-center justify-content-center">
                                <span class="far fa-stethoscope"></span>
                            </b-badge>-->
                <vtBubble
                  v-if="studyIncidentsCount"
                  qa="sample-processing-view-case-incidents-bubble"
                  :number="studyIncidentsCount"
                  :type="true"
                  :tooltip="' '+$tc('grid.detail.hasAlarms')"
                  class="ml-1"
                  @click="openIncidentModal($event, {coreEntity: 'CASE', casId: casId})"
                />
                <vtBubble
                  v-if="studyAttachmentsCount"
                  qa="sample-processing-view-case-attachments-bubble"
                  :number="studyAttachmentsCount"
                  :type="false"
                  :tooltip="' '+$tc('global.file',2)"
                  @click="showCaseAttachmentTreeModal"
                />
                <study-request-priority-dot
                  :priority="caseStudy.priority"
                  :size="9"
                  ta
                  class="mx-2"
                />
                <div class="d-flex align-items-center">
                  <case-status-badge
                    class="d-none d-lg-block"
                    :status="caseStudy.status"
                  />
                </div>
              </b-col>
              <b-col
                cols="auto"
                class="d-flex align-items-center justify-content-end"
              >
                <b-button
                  v-if="visibleCaseOperations"
                  id="case-grcard"
                  qa="sample-processing-view-case-ellipsis-button"
                  class="btn__icon btn__icon--primary px-1"
                >
                  <i
                    class="fas fa-ellipsis-v"
                    aria-hidden="true"
                  />
                </b-button>
                <b-popover
                  v-if="visibleCaseOperations"
                  target="case-grcard"
                  placement="bottom"
                  triggers="click blur"
                >
                  <div class="ap-popover">
                    <ul>
                      <li
                        v-if="$_canCreateIncident()"
                      >
                        <b-button
                          qa="sample-processing-view-add-incident-button"
                          :disabled="!$_canBeIncidentCreatedToCase(caseStudy)"
                          @click="openIncidentFormModal('CASE', {casId: casId})"
                        >
                          {{ $tc('registry.requestGrid.contextMenu.registerIncident') }}
                        </b-button>
                      </li>
                      <li
                        v-if="$_canAttachDocumentToCase()"
                      >
                        <b-button
                          qa="sample-processing-view-add-attachment-button"
                          :disabled="!$_canBeDocumentAttachedToNotValidatedCase(caseStudy)"
                          @click="showCaseAttachmentModal"
                        >
                          {{ $tc('registry.btn.addAttach') }}
                        </b-button>
                      </li>
                      <div class="horizontal-divider my-1" />
                      <li
                        v-if="$_canPrintCaseReport()"
                      >
                        <vt-loading-button
                          qa="sample-processing-view-report-button"
                          :params="{ promise: showAPReport, shortkeys: ['ctrl', 'shift', 'v'] }"
                        >
                          {{ $tc('registry.btn.viewAnaReport') }}
                        </vt-loading-button>
                      </li>
                      <li
                        v-if="caseStudy.requestLabel"
                      >
                        <b-button
                          qa="sample-processing-view-request-information-button"
                          @click="toggleRequestSidePanel()"
                        >
                          {{ $tc('global.requestInformation') }}
                        </b-button>
                      </li>
                      <li>
                        <b-button
                          qa="sample-processing-view-global-locator-button"
                          @click="showInGlobalLocator(caseStudy.casenumber)"
                        >
                          {{ $tc('global.showInGlobalLocator') }}
                        </b-button>
                      </li>
                    </ul>
                  </div>
                </b-popover>
              </b-col>
            </b-row>
            <div class="horizontal-divider" />
            <b-row class="py-2 px-4">
              <b-col
                class="entity-card__data col-lg-4 col-md-6"
              >
                <span class="entity-card__data--label">{{ $tc('grid.detail.patName') }}
                  <i
                    v-if="caseStudy.automaticPathologist"
                    v-tooltip="$tc('global.automaticAssignment')"
                    class="fal fa-magic ml-1"
                    aria-hidden="true"
                  />
                </span>
                <span
                  class="entity-card__data--text is-truncate"
                  qa="sample-processing-view-pathologist"
                >{{ caseStudy.pathologist ? caseStudy.pathologist : '-' }}</span>
              </b-col>
              <b-col
                class="entity-card__data col-lg-4 col-d-6"
              >
                <span class="entity-card__data--label">{{ $tc('registry.cases.resident') }}
                  <i
                    v-if="caseStudy.automaticResident"
                    v-tooltip="$tc('global.automaticAssignment')"
                    class="fal fa-magic ml-1"
                    aria-hidden="true"
                  />
                </span>
                <span
                  class="entity-card__data--text is-truncate"
                  qa="sample-processing-view-resident"
                >{{ caseStudy.resident ? caseStudy.resident : '-' }}</span>
              </b-col>
              <b-col
                class="entity-card__data d-none d-lg-flex"
              >
                <span class="entity-card__data--label">{{ $tc('grid.detail.casRegistrationDate') }}</span>
                <span
                  class="entity-card__data--text"
                  qa="sample-processing-view-registration-date"
                >{{ caseStudy.registrationDate ? caseStudy.registrationDate : '-' | datetime }}</span>
              </b-col>
            </b-row>
          </div>
        </b-col>
        <b-col class="col-md-6 col-12">
          <div class="entity-card">
            <b-row class="py-2 px-3">
              <b-col class="d-flex align-items-center">
                <b-button
                  class="btn__icon btn__icon--primary"
                  @click="showPatientModal"
                >
                  <i
                    class="far fa-user-circle mr-2 main"
                    aria-hidden="true"
                  />
                </b-button>
                <span
                  class="entity-card__title d-flex align-items-center font-weight--medium"
                  qa="sample-processing-view-patient-fullname"
                >
                  {{ caseStudy.patient.fullName }}
                </span>
                <vtBubble
                  v-if="patientIncidentsCount"
                  qa="sample-processing-view-patient-incidents-bubble"
                  :number="patientIncidentsCount"
                  :type="true"
                  :tooltip="' '+$tc('grid.detail.hasAlarms')"
                  class="ml-1"
                  @click="openIncidentModal($event)"
                />
                <vtBubble
                  v-if="patientAttachmentsCount"
                  qa="sample-processing-view-patient-attachments-bubble"
                  :number="patientAttachmentsCount"
                  :type="false"
                  :tooltip="' '+$tc('global.file',2)"
                  @click="showPatientAttachmentTreeModal"
                />
              </b-col>
              <b-col
                cols="auto"
                class="d-flex align-items-center justify-content-end"
              >
                <b-button
                  id="requestsCleanFilters"
                  v-tooltip="$tc('registry.cases.patientCases')"
                  qa="sample-processing-view-patient-previous-cases-button"
                  class="btn__icon btn__icon--primary is-relative"
                  @click="openPatientPreviousCases"
                >
                  <i
                    class="fas fa-history"
                    aria-hidden="true"
                  />
                  <div
                    v-if="patientPreviousCases"
                    class="btn-badge"
                  >
                    <span class="badge vt-nav-badge indicator-bubble badge-secondary">{{ patientPreviousCases }}</span>
                  </div>
                </b-button>

                <b-button
                  v-if="visiblePatientOperations"
                  id="patient-grcard-1"
                  qa="sample-processing-view-patient-ellipsis-button"
                  class="btn__icon btn__icon--primary px-1"
                >
                  <i
                    class="ml-2 fas fa-ellipsis-v"
                    aria-hidden="true"
                  />
                </b-button>
                <b-popover
                  v-if="visiblePatientOperations"
                  target="patient-grcard-1"
                  placement="bottom"
                  triggers="click blur"
                >
                  <div class="ap-popover">
                    <ul>
                      <li
                        v-if="$_canCreateIncident()"
                      >
                        <b-button
                          qa="sample-processing-view-patient-add-incident-button"
                          @click="openIncidentFormModal('PATIENT', {patId: caseStudy.patient.patId})"
                        >
                          {{ $tc('registry.requestGrid.contextMenu.registerIncident') }}
                        </b-button>
                      </li>
                      <li
                        v-if="$_canAttachDocumentToPatient()"
                      >
                        <b-button
                          qa="sample-processing-view-patient-add-attachment-button"
                          :disabled="!$_canBeDocumentAttachedToPatient(caseStudy && caseStudy.patient)"
                          @click="showPatientAttachmentModal"
                        >
                          {{ $tc('registry.btn.addAttach') }}
                        </b-button>
                      </li>
                    </ul>
                  </div>
                </b-popover>
              </b-col>
            </b-row>
            <div class="horizontal-divider" />
            <b-row class="py-2 px-4">
              <b-col
                v-if="globalParameters.mainPatientIdentifierType"
                class="entity-card__data"
              >
                <span class="entity-card__data--label">{{ globalParameters.mainPatientIdentifierType.name }}</span>
                <span
                  class="entity-card__data--text"
                  qa="sample-processing-view-patient-main-identifier"
                >{{ caseStudy.patient.mainPatientIdentifier ? caseStudy.patient.mainPatientIdentifier.paiIdentifier : '-' }}</span>
              </b-col>
              <b-col
                v-if="globalParameters.secondaryPatientIdentifierType"
                class="entity-card__data"
              >
                <span class="entity-card__data--label">{{ globalParameters.secondaryPatientIdentifierType.name }}</span>
                <span
                  class="entity-card__data--text"
                  qa="sample-processing-view-patient-secondary-identifier"
                >{{ caseStudy.patient.secondaryPatientIdentifier ? caseStudy.patient.secondaryPatientIdentifier.paiIdentifier : '-' }}</span>
              </b-col>
              <b-col
                class="entity-card__data"
              >
                <span class="entity-card__data--label">{{ $tc('entities.patient.gender') }}</span>
                <span
                  class="entity-card__data--text"
                  qa="sample-processing-view-patient-gender"
                >{{ caseStudy.patient.genderName ? caseStudy.patient.genderName : caseStudy.patient.gender.name }}</span>
              </b-col>
              <b-col
                class="entity-card__data"
              >
                <span class="entity-card__data--label">{{ $tc('entities.patient.birthdate') }}</span>
                <span
                  class="entity-card__data--text"
                  qa="sample-processing-view-patient-birthdate"
                >{{ caseStudy.patient.patBirthdate | date }}</span>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-form-row>
      <!--Grossing Main Layout Content-->
      <b-row class="mainLayout__grossing-area">
        <b-col class="customHeight">
          <b-card class="mainCard float-component">
            <b-row class="grossing-container">
              <!--Sample Cards Column-->
              <b-col
                class="h-100 right-bordered"
                :class="showCompactCardsView ? 'col-auto cardsCompact pt-0 pl-0 pr-0' : 'col-3 col-md-3 col-lg-2 cardsColumn pt-1 px-0'"
              >
                <div
                  class="sample-btns px-2"
                  :class="showCompactCardsView ? 'd-flex justify-content-center' : 'd-flex justify-content-start'"
                >
                  <b-button
                    v-if="$_canEditSample() && $refs.samplesCards"
                    v-tooltip="$tc('grossing.modal.changeResponsible')"
                    qa="sample-processing-view-responsible-button"
                    class="btn__icon btn__icon--primary is-relative  px-2"
                    :disabled="isReadOnly || !$refs.samplesCards.isAnySamplePendingGrossing"
                    @click="showChangeResponsible"
                  >
                    <i
                      class="far fa-user-friends"
                      aria-hidden="true"
                    />
                    <i
                      class="far fa-pen edition-icon"
                      aria-hidden="true"
                    />
                  </b-button>
                </div>
                <div class="sample-cards-scrolling-area">
                  <samples-cards
                    id="samplesCards"
                    ref="samplesCards"
                    :laboratory-id="laboratoryIdLogged"
                    :route-is-grossing="routeIsGrossing"
                    :route-is-laboratory="routeIsLaboratory"
                    :case-id="casId"
                    :sample-id="samId"
                    :can-disable-cards="true"
                    class="mx-2"
                    :show-compact-view="showCompactCardsView"
                    @showIncidentsModal="openIncidentModal($event, {coreEntity: 'SAMPLE', casId: casId, samId: $event})"
                    @show-sample-attachment-tree-modal="showSampleAttachmentTreeModal($event)"
                    @showReviewInfoModal="onRevisionClick($event)"
                    @cardSelected="navigateToSample(casId, $event.samId)"
                  />
                </div>
                <div
                  :class="showCompactCardsView ? 'cards-footer-contract' : 'cards-footer'"
                  class="d-flex align-items-center justify-content-center"
                >
                  <b-button
                    v-shortkey="['f2']"
                    v-tooltip="showCompactCardsView ? $tc('global.extend') : $tc('global.contract')"
                    :class="showCompactCardsView ? 'btn__icon btn__icon--primary pr-1' : 'btn__icon btn__icon--primary'"
                    @shortkey.prevent="showCompactCardsView = !showCompactCardsView"
                    @click="showCompactCardsView = !showCompactCardsView"
                  >
                    <i
                      v-if="!showCompactCardsView"
                      qa="sample-processing-view-compact-sample-cards-button"
                      class="fal fa-chevron-double-left"
                      aria-hidden="true"
                    />
                    <i
                      v-if="showCompactCardsView"
                      qa="sample-processing-view-expand-sample-cards-button"
                      class="fal fa-chevron-double-right"
                      aria-hidden="true"
                    />
                  </b-button>
                </div>
              </b-col>
              <!--Tabs Column-->
              <b-col
                v-if="showTabs"
                class="tabsColumn h-100"
              >
                <b-row class="tabsRow">
                  <b-col class="h-100">
                    <b-tabs
                      :key="samId"
                      pills
                      card
                      vertical
                      class="vt-tabs--vertical"
                      :value="activeTabIndex"
                    >
                      <b-tab
                        ref="sampleTab"
                        title-item-class="manuallyControlledTab"
                      >
                        <template slot="title">
                          <i
                            qa="sample-processing-view-sample-tab"
                            class="section-icon fal fa-prescription-bottle"
                            aria-hidden="true"
                          />
                        </template>
                        <div
                          v-shortkey="['ctrl', '1']"
                          class="section-content py-2 px-4"
                          @shortkey.prevent="shortKeyPressed(TAB_INDEX.SAMPLE)"
                        >
                          <sample-processing-tab-sample
                            v-if="activeTabIndex === TAB_INDEX.SAMPLE && caseStudy"
                            ref="sampleForm"
                            :disabled="isReadOnly"
                            :hide="hidePeople"
                            :laboratory-id="laboratoryIdLogged"
                            :case-study="caseStudy"
                            :sample="sample"
                            :route-is-grossing="routeIsGrossing"
                            :route-is-laboratory="routeIsLaboratory"
                            @openIncidentFormModal="openIncidentFormModal('SAMPLE', $event)"
                            @sample-attachment-added="reloadSampleCards"
                            @creatingSubsampleMode="requestSubsampleMode"
                            @sampleChanged="loadSample"
                          />
                        </div>
                      </b-tab>
                      <b-tab
                        v-if="!creatingSubsampleMode"
                        ref="macroTab"
                        title-item-class="manuallyControlledTab"
                        class="sample-processing-macro-tab"
                      >
                        <template slot="title">
                          <i
                            qa="sample-processing-view-macroscopy-tab"
                            class="section-icon fal fa-align-justify"
                            aria-hidden="true"
                          />
                          <div
                            v-if="sectionsSummary && sectionsSummary.macroHasData"
                            class="section-bubble"
                          />
                        </template>
                        <div
                          v-shortkey="['ctrl', '2']"
                          class="section-content description-content py-2 px-4"
                          @shortkey.prevent="shortKeyPressed(TAB_INDEX.MACRO)"
                        >
                          <description-editor
                            v-if="activeTabIndex === TAB_INDEX.MACRO"
                            ref="macroEditor"
                            :disabled="isReadOnly"
                            :hide-attach-operations="!$_canAttachDocumentToSample()"
                            :disable-attach-operations="!$_canBeDocumentAttachedToSample(sample)"
                            :hide-edit-operations="!$_canEditSample()"
                            :laboratory-id="laboratoryIdSample"
                            :case-id="casId"
                            :description-title="$tc('grossing.content.description')"
                            :description-service="sampleMacroService"
                            :parent-entity-ids-array="[laboratoryIdSample, casId, samId]"
                            :root-filename-for-audio-description="`${sample.sampleNumber}-MacroAudio-`"
                            :attachment-type-id-for-description-audio="attachmentTypeIdForMacroAudio"
                            :attachment-service-for-audio-attachments="sampleAttachmentService"
                            :sample-id="samId"
                            text-template-modal-ui-component-code="uicomponent.grossing.templateconfig"
                            :attachment-service="attachmentService"
                            :modal-confirmation-state-ui-component-code="modalConfirmationStateUiComponentCode"
                            :modal-confirmation-state-store="modalConfirmationStateStore"
                            :modal-confirmation-state-name="modalConfirmationStateName"
                          />
                        </div>
                      </b-tab>
                      <b-tab
                        v-if="!creatingSubsampleMode"
                        ref="portionTab"
                        title-item-class="manuallyControlledTab"
                      >
                        <template slot="title">
                          <i
                            qa="sample-processing-view-portions-tab"
                            class="section-icon icon-block"
                            aria-hidden="true"
                          />
                          <div
                            v-if="sectionsSummary && sectionsSummary.hasPortions"
                            class="section-bubble"
                          />
                        </template>
                        <div
                          v-shortkey="['ctrl', '3']"
                          class="section-content py-2 px-4"
                          @shortkey.prevent="shortKeyPressed(TAB_INDEX.PORTION)"
                        >
                          <sample-processing-tab-portion-editable-grid
                            v-if="activeTabIndex === TAB_INDEX.PORTION"
                            ref="portionGrid"
                            :disabled="isReadOnly"
                            :laboratory-id="laboratoryIdLogged"
                            :patient="caseStudy.patient"
                            :sample="sample"
                            :case-id="casId"
                            :priority="caseStudy.priority"
                            @portionsUpdated="reloadCase"
                            @subportionsUpdated="reloadCase"
                            @subportionsAdded="reloadCase"
                            @showIncidentsModal="openIncidentModal($event)"
                            @showIncidentFormModal="openIncidentFormModal('PORTION', $event)"
                          />
                        </div>
                      </b-tab>
                      <b-tab
                        v-if="!creatingSubsampleMode && routeIsGrossing"
                        ref="mediaTab"
                        title-item-class="manuallyControlledTab"
                      >
                        <template slot="title">
                          <i
                            qa="sample-processing-view-image-video-tab"
                            class="section-icon fal fa-photo-video"
                            aria-hidden="true"
                          />
                          <div
                            v-if="sectionsSummary && sectionsSummary.hasMacroImagesOrVideos"
                            class="section-bubble"
                          />
                        </template>
                        <div
                          v-shortkey="['ctrl', '4']"
                          class="section-content py-2 px-4"
                          @shortkey.prevent="shortKeyPressed(TAB_INDEX.MEDIA)"
                        >
                          <SampleProcessingTabPhoto
                            v-if="activeTabIndex === TAB_INDEX.MEDIA"
                            id="grossingTabPhoto"
                            ref="grossingTabPhoto"
                            :laboratory-id="laboratoryIdLogged"
                            :case-id="casId"
                            :sample="sample"
                            :is-case-validated="$_isCaseValidated(caseStudy && caseStudy.status.code)"
                            @sample-attachment-added="reloadSampleCards"
                            @sample-attachments-deleted="reloadSampleCards"
                          />
                        </div>
                      </b-tab>
                    </b-tabs>
                  </b-col>
                  <!--Breadcrumb-->
                  <b-col
                    cols="auto"
                    class="px-1 left-bordered"
                    style="border-left:1px solid #d8d8d8;"
                  >
                    <VtBreadCrumbs
                      v-if="casId"
                      :lab-id="laboratoryIdLogged"
                      :case-study="caseStudy"
                      :samples="getSamples"
                    />
                  </b-col>
                </b-row>
                <b-row class="grossing-footer d-flex align-items-center">
                  <b-col>
                    <div class="row estudio-footer">
                      <b-col
                        v-if="!creatingSubsampleMode && canBeDeleted()"
                      >
                        <button
                          qa="sample-processing-view-delete-button"
                          type="button"
                          class="btn btn__icon btn__icon--primary error btn-secondary"
                          @click="deleteSubsample"
                        >
                          {{ $tc('global.delete') }}
                        </button>
                      </b-col>
                      <b-col class="text-right d-flex align-items-center justify-content-end">
                        <vt-loading-button
                          class="btn__outline btn-sm d-none"
                          :params="{ promise: showAPReport, shortkeys: ['ctrl', 'shift', 'v'] }"
                        >
                          {{ $tc('registry.btn.viewAnaReport') }}
                        </vt-loading-button>
                        <vt-loading-button
                          v-if="$_canEditSample() || ($_canEditPortion() && activeTabIndex === TAB_INDEX.PORTION)"
                          qa="sample-processing-view-save-button"
                          class="btn__outline btn-sm mr-2"
                          :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code) && ![TAB_INDEX.PORTION, TAB_INDEX.SAMPLE].includes(activeTabIndex)"
                          :params="{ promise: saveCurrentTab, shortkeys: ['alt', 's'] }"
                        >
                          <i
                            class="far fa-save mr-2"
                            aria-hidden="true"
                          />
                          {{ $tc('global.save') }}
                        </vt-loading-button>
                        <vt-loading-button
                          v-if="!creatingSubsampleMode && $_canProcessSample()"
                          qa="sample-processing-view-process-button"
                          :disabled="_disabledProcessButton"
                          class="btn--primary btn-sm"
                          :params="{ promise: onProcessSample, shortkeys: ['ctrl', 'enter'] }"
                        >
                          <i
                            class="far fa-check mr-2"
                            aria-hidden="true"
                          />
                          {{ processButtonTranslation }}
                        </vt-loading-button>
                      </b-col>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                v-else-if="!loading"
                class="px-0 no-grossing-empty-state"
              >
                <b-container
                  class="h-100"
                  fluid
                >
                  <b-row class="h-100">
                    <b-col>
                      <div class="no-grossing-empty-box d-flex align-items-center justify-content-center">
                        <div>
                          <div class="d-flex align-items-center justify-content-center mb-5">
                            <div class="circle" />
                            <samples />
                          </div>
                          <span class="header-title-3">{{ $tc('laboratory.emptyState.sampleTypeNotAvailable') }}</span>
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      cols="auto"
                      class="px-1 d-flex align-items-center"
                      style="border-left:1px solid #d8d8d8;"
                    >
                      <VtBreadCrumbs
                        v-if="caseStudy"
                        :lab-id="laboratoryIdLogged"
                        :case-study="caseStudy"
                        :samples="getSamples"
                      />
                    </b-col>
                  </b-row>
                </b-container>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!--Grossing Modals-->
    <grossing-responsible-modal
      ref="responsibleModal"
      :message="true"
      :route-is-grossing="routeIsGrossing"
      @reloadGrid="reloadSampleForm"
    />
    <!-- TODO Oculto para poder abrir Tallado. Esperando a end-points para volverlo a mostrar-->
    <!-- <modal-patient-cases
      ref="modalPatientCases"
      id="modalPatientCases"
      :patient="caseStudy.patient"
    ></modal-patient-cases> -->
    <modal-patient-card
      v-if="caseStudy.patient"
      id="modalPatientCard"
      ref="modalPatientCard"
      :patient-id="caseStudy.patient.patId"
    />
    <modal-case-card
      id="modalCaseCard"
      ref="modalCaseCard"
      :case-study="caseStudy"
    />
    <common-attachment-tree-modal
      v-if="caseStudy.patient"
      ref="attachmentTreeModal"
      :patient="caseStudy.patient"
      :laboratory-id="laboratoryIdLogged"
      @attachments-updated="reloadCaseAndSampleCards"
    />
    <common-incident-modal-tree
      ref="incidentModalTree"
      @incident-added="reloadCaseAndSampleCards"
    />
    <incident-form-modal
      v-if="coreEntity"
      id="incidentFormModal"
      ref="incidentFormModal"
      :core-entity="coreEntity"
      @incident-added="reloadCaseAndSampleCards"
    />
    <modal-confirmation
      id="confirmationModal"
      ref="confirmationModal"
      :content="message"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="coreEntity"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded()"
      />
    </modal-wrapper>
    <modal-wrapper ref="reviewInfoModalWrapper">
      <review-info-modal
        :laboratory-id="laboratoryIdLogged"
        :case-id="casId"
      />
    </modal-wrapper>
    <modal-unsaved-changes
      id="grossingUnsavedChangesModal"
      ref="unsavedChangesModal"
    />
    <search-patient-modal
      ref="searchPatientModal"
      id-modal="searchPatientModal"
      @closePreviousCasesWindow="closePreviousCasesWindow"
    />
    <sample-batch-processing
      ref="sampleBatchProcessing"
      :is-grossing-route="routeIsGrossing"
      @samplesUpdated="reloadCaseAndSampleCards()"
    />

    <modal-wrapper ref="commonPetitionerListModalDetailWrapper">
      <common-petitioner-list-modal-detail ref="commonPetitionerListModalDetail" />
    </modal-wrapper>
  </div>
</template>

<script>
import SearcherModule from '@/components/SearcherModule'
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import ModalCaseCard from '@/modules/grossingModule/modals/ModalCaseCard'
import CaseStatusBadge from '@/components/common/CommonCaseStatusBadge'
import StudyRequestPriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'
import SamplesCards from '@/components/samplesCards/SamplesCards'
import SampleProcessingTabSample from '@/modules/sampleProcessingModule/views/form/SampleProcessingTabSample'
import DescriptionEditor from '@/components/description/DescriptionEditor'
import SampleProcessingTabPortionEditableGrid
    from '@/modules/sampleProcessingModule/views/form/SampleProcessingTabPortionEditableGrid'
import SampleProcessingTabPhoto from '@/modules/sampleProcessingModule/views/form/SampleProcessingTabPhoto'
import VtBreadCrumbs from '@/components/common/VtBreadCrumbs'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import VtBubble from '@/components/common/VtBubble'
import { SAMPLE_STATES } from '@/api/resources/entityState/sample-state'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import AddFileModal from '@/components/AddFileModal'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import sampleProcessingPortionService from '@/modules/sampleProcessingModule/services/sampleProcessingPortionService'
import GrossingResponsibleModal from '@/modules/grossingModule/modals/GrossingResponsibleModal'
import sampleMacroService from '@/api/resources/description/SampleMacroService'
import attachmentTypeService from '@/api/resources/attachment/attachmentTypeService'
import sampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import { getSampleDeletionConfirmationMessage } from '@/api/resources/ConfirmationMessageResource'
import ModalUnsavedChanges from '@/components/modals/ModalUnsavedChanges'
import SearchPatientModal from '@/components/searcherPatientModal/SearchPatientModal'
import Encoder from '@/utils/encoder'
import commonCaseServices from '@/api/resources/common/commonCaseServices'
import StudyRequestDetail from '@/components/common/detail/CommonRequestDetail'
import commonSampleService from '@/api/resources/common/commonSampleService'
import { WorkflowTypeEnum } from '@/api/services/common/sample-type/common-sample-type.dto'
import sampleMacroAttachmentService from '@/api/resources/description/attachment/sampleMacroAttachmentService'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import sampleProcessingCaseService from '@/modules/sampleProcessingModule/services/sampleProcessingCaseService'
import sampleProcessingSampleService from '@/modules/sampleProcessingModule/services/sampleProcessingSampleService'
import Samples from '@/assets/images/svg/Samples'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { PatientOperationGuardMixin } from '@/api/operations/mixins/guards/patient-operation-guard.mixin'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { PortionOperationGuardMixin } from '@/api/operations/mixins/guards/portion-operation-guard.mixin'
import SampleBatchProcessing from '@/modules/laboratoryModule/grid/SampleBatchProcessing'
import laboratorySidebarConfig from '@/modules/laboratoryModule/config/sidebarConfig'
import { SampleOperation } from '@/api/operations/models/sample-operation'
import ReviewInfoModal from '@/modules/diagnosisModule/views/form/modals/reviewModal/ReviewInfoModal'
import CommonPetitionerListModalDetail from '@/components/modals/CommonPetitionerListModalDetail/CommonPetitionerListModalDetail.vue'
import { cloneDeep } from 'lodash'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'
import { router } from '@/router'
import { store } from '@/store/store'
import { HelperUtils } from '@/shared/util/helper-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'

export const SAMPLE_FORM_TAB_INDEX = 0
export const MACRO_TAB_INDEX = 1
export const PORTION_TAB_INDEX = 2
export const MEDIA_TAB_INDEX = 3

export default {
    name: 'SampleProcessingSampleView',
    components: {
        SearcherModule,
        CaseStatusBadge,
        StudyRequestPriorityDot,
        SamplesCards,
        SampleProcessingTabSample,
        DescriptionEditor,
        SampleProcessingTabPortionEditableGrid,
        SampleProcessingTabPhoto,
        VtBreadCrumbs,
        ModalCaseCard,
        CommonAttachmentTreeModal,
        ModalPatientCard,
        VtBubble,
        ModalConfirmation,
        AddFileModal,
        CommonIncidentModalTree,
        IncidentFormModal,
        GrossingResponsibleModal,
        ModalUnsavedChanges,
        SearchPatientModal,
        StudyRequestDetail,
        Samples,
        SampleBatchProcessing,
        ReviewInfoModal,
        CommonPetitionerListModalDetail
    },
    mixins: [
        SampleOperationGuardMixin,
        CaseOperationGuardMixin,
        PortionOperationGuardMixin,
        IncidentOperationGuardMixin,
        PatientOperationGuardMixin
    ],
    beforeRouteUpdate(to, from, next) {
        this.checkBeforeRoute(next)
    },
    beforeRouteLeave(to, from, next) {
        this.checkBeforeRoute(next)
    },
    data() {
        return {
            loading: false,
            casId: null,
            samId: null,
            showCompactCardsView: false,
            hidePeople: true,
            caseStudy: null,
            creatingSubsampleMode: false,
            coreEntity: null,
            encoder: new Encoder(),
            sample: null,
            message: null,
            attachmentTypeIdForMacroAudio: null,
            activeTabIndex: SAMPLE_FORM_TAB_INDEX,
            patientPreviousCases: null,
            toggleSidepanel: false,
            modalConfirmationStateUiComponentCode: '',
            modalConfirmationStateName: '',
            modalConfirmationStateStore: '',
            entitiesIdArray: [],
            attachmentService: sampleMacroAttachmentService,
            sectionsSummary: null,
            patientPreviousCasesWindowsReference: null,
            grossingRouteName: 'grossingSampleView',
            laboratoryRouteName: 'laboratorySampleView'
        }
    },
    computed: {
        getSamples() {
            return this.$refs.samplesCards?.samples
        },
        showTabs() {
            return !this.loading && (
                (this.sample?.sampleType.workflow === WorkflowTypeEnum.GROSSING_BIOPSIES && this.routeIsGrossing) ||
                  (this.sample?.sampleType.workflow === WorkflowTypeEnum.PROCESSING_CYTOLOGIES && this.routeIsLaboratory)
            )
        },
        TAB_INDEX() {
            return {
                SAMPLE: SAMPLE_FORM_TAB_INDEX,
                MACRO: MACRO_TAB_INDEX,
                PORTION: PORTION_TAB_INDEX,
                MEDIA: MEDIA_TAB_INDEX
            }
        },
        laboratoryIdLogged() {
            return store.state.labId
        },
        laboratoryIdSample() {
            return this.sample?.currentLaboratory.labId
        },
        studyIncidentsCount() {
            return this.caseStudy?.incidentsCount ?? 0
        },
        studyAttachmentsCount() {
            return this.caseStudy?.attachmentsCount ?? 0
        },
        patientIncidentsCount() {
            return this.caseStudy?.patient.incidentsCount ?? 0
        },
        patientAttachmentsCount() {
            return this.caseStudy?.patient.attachmentsCount ?? 0
        },
        regex() {
            return store.state.regex
        },
        sampleMacroService() {
            return sampleMacroService
        },
        sampleAttachmentService() {
            return sampleAttachmentService
        },
        isReadOnly() {
            return !this.$_isCaseEditable(this.caseStudy?.status.code) ||
                this.laboratoryIdSample !== this.laboratoryIdLogged
        },
        routeIsGrossing() {
            return this.$route.name === this.grossingRouteName
        },
        routeIsLaboratory() {
            return this.$route.name === this.laboratoryRouteName
        },
        processButtonTranslation() {
            if (this.routeIsGrossing) {
                return this.$tc('global.finalize')
            }
            return this.$tc('sampleProcessing.process')
        },
        _disabledProcessButton() {
            return !this.sample?.metadata.operationGuards.canBeProcessed &&
                !this.sample?.metadata.operationGuards.canBeReprocessed &&
                !this.sample?.metadata.operationGuards.canBeInsertedIntoProcessor &&
                !this.sample?.metadata.operationGuards.canBeRemovedFromProcessor
        },
        visibleCaseOperations() {
            return this.$_canCreateIncident() ||
                this.$_canAttachDocumentToCase()
        },
        visiblePatientOperations() {
            return this.$_canCreateIncident() ||
                this.$_canAttachDocumentToPatient()
        }
    },
    watch: {
        '$route.params': function() {
            this.creatingSubsampleMode = false
            this.loadData()
        }
    },
    created() {
        this.$eventHub.$on('changeRouteWithCheckChanges', this.checkIfDataHasBeenChangedWhenChangedLaboratoryAndGoToRoute)
    },
    async mounted() {
        this.loading = true

        this.modalConfirmationStateName = this.$route.meta.modalConfirmationStateName
        this.modalConfirmationStateStore = this.$route.meta.modalConfirmationStateStore
        this.modalConfirmationStateUiComponentCode = this.$route.meta.modalConfirmationStateUiComponentCode

        this.updateLoading(true)
        await this.loadData()
        await this.getPatientCases()
        this.updateLoading(false)

        if (this.$route.params.portionId || this.$route.params.porId) {
            this.activeTabIndex = PORTION_TAB_INDEX
        }

        viewerService.closeViewersOfDifferentCase(`${this.casId}`)

        this.loading = false

        this.$eventHub.$on('updatePatientInformation', this.onUpdatedPatient)
    },
    beforeDestroy() {
        this.$eventHub.$off('changeRouteWithCheckChanges', this.checkIfDataHasBeenChangedWhenChangedLaboratoryAndGoToRoute)
        this.$eventHub.$off('updatePatientInformation', this.onUpdatedPatient)
    },
    methods: {
        checkBeforeRoute(goToRoute) {
            if (!router.currentRoute.meta.skipChecking) {
                this.checkChangesInActiveTabAndApplyAction(goToRoute)
            } else {
                router.currentRoute.meta.skipChecking = false
                goToRoute()
            }
        },
        onUpdatedPatient(updatedPatient) {
            if (updatedPatient.patId === this.caseStudy.patient.patId) {
                this.caseStudy.patient = cloneDeep(updatedPatient)
            }
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        onRevisionClick(event) {
            this.$refs.reviewInfoModalWrapper.show([event.revision, event.entitySelected, event.entityType])
        },
        openPatientPreviousCases() {
            this.closePreviousCasesWindow()
            const routeData = router.resolve({
                name: 'patientCases',
                params: { patId: this.caseStudy.patient.patId, labId: this.laboratoryIdLogged }
            })
            this.patientPreviousCasesWindowsReference = window.open(routeData.href, '_blank')
        },
        async getSectionsSummary() {
            const response =
                await PromiseUtils.toCallSecure(
                    sampleProcessingSampleService.sampleSectionsSummary(
                        this.laboratoryIdLogged,
                        this.casId,
                        this.samId))
            if (response !== null) {
                this.sectionsSummary = response
            }
        },
        async getPatientCases() {
            const response =
                await PromiseUtils.toCallSecure(
                    commonCaseServices.patientPreviousCasesCountByLaboratory(
                        this.laboratoryIdLogged,
                        this.caseStudy.patient.patId))
            if (response !== null) {
                this.patientPreviousCases = response.count
            }
        },
        toggleRequestSidePanel() {
            this.$root.$emit('bv::hide::popover', 'case-grcard')
            this.toggleSidepanel = !this.toggleSidepanel
        },
        closeRequestSidePanel() {
            this.toggleSidepanel = false
        },
        openPatientModal() {
            this.$refs.searchPatientModal.show()
        },
        openIncidentFormModal(coreEntity, entitiesIds) {
            this.coreEntity = coreEntity
            this.$nextTick(() => this.$refs.incidentFormModal.show(null, entitiesIds))
        },
        openIncidentModal(event, defaultOption = null) {
            const optionToFocus = this.$_isEmpty(event) ? defaultOption : event
            this.$refs.incidentModalTree.show(optionToFocus, this.caseStudy.patient.patId)
        },
        async reloadSampleForm() {
            await this.loadSample()
            this.$emit('updateSummary')
        },
        async reloadSampleCards() {
            await this.$refs.samplesCards?.loadSamples()
            await this.getSectionsSummary()
        },
        async showAPReport() {
            const getCaseReportPDFParams = {
                laboratoryId: this.laboratoryIdLogged,
                caseId: this.casId,
                caseLabel: this.caseStudy?.casenumber,
                print: false
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
        },
        async showChangeResponsible() {
            const samples = await this.getSamplesForResponsibleModal()
            if (samples) {
                this.$refs.responsibleModal.show(samples)
            }
        },
        async getSamplesForResponsibleModal() {
            this.updateLoading(true)
            const [err, samples] = await this.toCall(commonSampleService.getSamples(this.laboratoryIdLogged, this.caseStudy.id))
            this.updateLoading(false)
            if (err || !samples.length) {
                this.showErrorModalGlobal((err && err.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND) ? err.response.data.message : this.$tc('component.modalError.title'))
                return
            }
            const filteredSamples = this.routeIsGrossing ? samples.filter((sample) => sample.sampleType.workflow === WorkflowTypeEnum.GROSSING_BIOPSIES)
                : samples.filter((sample) => sample.sampleType.workflow === WorkflowTypeEnum.PROCESSING_CYTOLOGIES)
            if (!filteredSamples.length) {
                return
            }
            return filteredSamples.map(sample => {
                return {
                    samId: sample.id,
                    samLabel: sample.sampleLabel,
                    caseStudy: { casId: this.caseStudy.id },
                    originalConcurrencyUpdatedAt: sample.originalConcurrencyUpdatedAt,
                    grossingResponsible: sample.responsibleForGrossing,
                    grossingTutor: sample.grossingTutor,
                    grossingTechController: sample.grossingControllerTechnician,
                    grossingCoordinator: this.sample.grossingCoordinator
                }
            })
        },
        showPatientModal() {
            this.$refs.modalPatientCard.show()
        },
        showCaseModal() {
            this.$refs.modalCaseCard.show()
        },
        showPatientAttachmentTreeModal() {
            this.$refs.attachmentTreeModal.showPatient()
        },
        showCaseAttachmentTreeModal() {
            this.$refs.attachmentTreeModal.showCase(this.casId)
        },
        showSampleAttachmentTreeModal(sampleId) {
            this.$refs.attachmentTreeModal.showSample(sampleId)
        },
        async internalSaveCurrentTab() {
            const activeTabContent = this.getActiveTabContent()

            if (this.creatingSubsampleMode && this.activeTabIndex === SAMPLE_FORM_TAB_INDEX) {
                await activeTabContent.saveNewSubsample()
                return false
            }

            return activeTabContent.save()
        },
        async saveCurrentTab() {
            const activeTabContent = this.getActiveTabContent()
            if (!activeTabContent.hasBeenChanged()) {
                return true
            }
            const successfulSave = await this.internalSaveCurrentTab()
            if (successfulSave && this.activeTabIndex === SAMPLE_FORM_TAB_INDEX) {
                this.$emit('updateSummary')
                await this.loadData()
            }
            this.$nextTick(() => this.$refs.searcherModule.focusInput())
            await this.getSectionsSummary()
            return successfulSave
        },
        async onProcessSample() {
            const savedOk = await this.saveCurrentTab()
            if (savedOk) {
                // TODO: refactorizar SampleBatchProcessing y extraer factor común para procesamiento en lote y procesamiento individual
                const sampleToProcess = {
                    ...this.sample,
                    grossingResponsible: this.sample.grossingResponsible,
                    caseStudy: { casId: this.sample.caseId },
                    sampleLabel: this.sample.label
                }
                this.$nextTick(() =>
                    this.$refs.sampleBatchProcessing.processSamples([sampleToProcess], this.getSidebarOptionForProcessingSample()))
            }
        },
        getSidebarOptionForProcessingSample() {
            if (this.sample.metadata.operationGuards.canBeProcessed) {
                return laboratorySidebarConfig.find(option => option.id === SampleOperation.PROCESS)
            }
            if (this.sample.metadata.operationGuards.canBeReprocessed) {
                return laboratorySidebarConfig.find(option => option.id === SampleOperation.REPROCESS)
            }
            if (this.sample.metadata.operationGuards.canBeInsertedIntoProcessor) {
                return laboratorySidebarConfig.find(option => option.id === SampleOperation.TO_PROCESSOR)
            }
            if (this.sample.metadata.operationGuards.canBeRemovedFromProcessor) {
                return laboratorySidebarConfig.find(option => option.id === SampleOperation.FROM_PROCESSOR)
            }
            return null
        },
        isResponsibleForGrossingValid() {
            return store.state.userInformation?.personnel?.id === this.sample.grossingResponsible?.id
        },
        showPatientAttachmentModal() {
            this.coreEntity = ATTACHMENT_ENTITY_TYPE.PATIENT
            this.entitiesIdArray = [this.caseStudy.patient.patId]
            this.$refs.addFileModalWrapper.show()
        },
        showCaseAttachmentModal() {
            this.coreEntity = ATTACHMENT_ENTITY_TYPE.CASE
            this.entitiesIdArray = [this.laboratoryIdLogged, this.caseStudy.id]
            this.$refs.addFileModalWrapper.show()
        },
        async onAttachmentsAdded() {
            await this.reloadCase()
        },
        async reloadCase() {
            const [err, response] = await this.toCall(sampleProcessingCaseService.getCaseWithPatientByCaseId(
                this.laboratoryIdLogged,
                this.casId
            ))
            if (err) {
                this.showErrorModalGlobal()
                this.updateLoading(false)
                return
            }
            this.caseStudy = response
        },
        async reloadCaseAndSampleCards() {
            const currentActiveTabIndex = this.activeTabIndex
            this.activeTabIndex = null
            await this.loadData()
            this.$nextTick(() => { this.activeTabIndex = currentActiveTabIndex })
        },

        async onPortionSearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    sampleProcessingPortionService.searchPortionByLabel(
                        this.laboratoryIdLogged,
                        searchInformation.search),
                    error => this.$_showErrorModal(
                        error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND
                            ? this.$tc('component.searcher.error.noPortion')
                            : undefined))

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IdentifierTypeScope.PORTION,
                            entityValue: entity,
                            laboratoryId: entity.sample.caseStudy.laboratory.labId
                        }))
                    entitySelected = await this.$_showMultiEntitySelection(searchInformation.search, foundEntities)
                }

                if (this.samId === entitySelected.sample.samId) {
                    this.checkChangesInActiveTabAndApplyAction(
                        () => { this.activeTabIndex = PORTION_TAB_INDEX })
                    this.$nextTick(() => this.$refs.portionGrid.selectPortion(entitySelected.porId))
                    return
                }

                await router.push({
                    name: this.routeIsGrossing ? 'grossingSampleView' : 'laboratorySampleView',
                    params: {
                        casId: entitySelected.sample.caseStudy.casId,
                        samId: entitySelected.sample.samId,
                        porId: entitySelected.porId
                    }
                })
            }
        },
        async onSampleSearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    sampleProcessingSampleService.searchSampleBySampleLabel(
                        this.laboratoryIdLogged,
                        searchInformation.search),
                    error => this.$_showErrorModal(
                        error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND
                            ? this.$tc('component.searcher.error.noSample')
                            : undefined))

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IdentifierTypeScope.SAMPLE,
                            entityValue: entity,
                            laboratoryId: entity.caseStudy.laboratory.labId
                        }))
                    entitySelected = await this.$_showMultiEntitySelection(searchInformation.search, foundEntities)
                }

                if (entitySelected.samId === this.sample?.id) {
                    this.$_showWarningModal(this.$tc('grossing.error.sampleAlreadyLoaded'))
                    return
                }

                await this.navigateToSample(entitySelected.caseStudy.casId, entitySelected.samId)
            }
        },
        async onStudySearch(searchInformation) {
            if (this.routeIsGrossing) {
                const response =
                    await this.$_toCallUnique(
                        sampleProcessingCaseService.searchCaseByCasenumber(
                            this.laboratoryIdLogged,
                            searchInformation.search),
                        error => {
                            if (error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND) {
                                this.$_showErrorModal(this.$tc('component.searcher.error.noCase'))
                            } else {
                                this.$_showErrorModalFromErrorResponse(error)
                            }
                        })

                if (response?.length) {
                    let entitySelected = response[0]
                    if (response.length > 1) {
                        const foundEntities =
                            response.map(entity => ({
                                entityType: IdentifierTypeScope.CASE,
                                entityValue: entity,
                                laboratoryId: entity.laboratory.labId
                            }))
                        entitySelected = await this.$_showMultiEntitySelection(searchInformation.search, foundEntities)
                    }

                    if (entitySelected.casId === this.caseStudy.id) {
                        this.$_showWarningModal(this.$tc('grossing.error.caseAlreadyLoaded'))
                        return
                    }

                    /**
                     * navigate to first sample whose laboratory is the same as the logged-in user,
                     * if not show first available sample
                     */
                    const samplesCurrentLaboratory =
                        entitySelected.samples.filter(
                            sample => sample.currentLaboratory.labId === this.laboratoryIdLogged)
                    const sample = samplesCurrentLaboratory.length
                        ? samplesCurrentLaboratory[0]
                        : entitySelected.samples[0]

                    await this.navigateToSample(entitySelected.casId, sample.samId)
                }
            }
        },

        closePreviousCasesWindow() {
            if (this.patientPreviousCasesWindowsReference) {
                this.patientPreviousCasesWindowsReference.close()
            }
        },
        checkChangesInActiveTabAndApplyAction(actionIfTrue) {
            this.closePreviousCasesWindow()
            if (this.creatingSubsampleMode) {
                actionIfTrue()
                return
            }
            const activeTabContent = this.getActiveTabContent()
            if (!activeTabContent) {
                actionIfTrue()
                return
            }
            if (activeTabContent.hasBeenChanged()) {
                this.$refs.unsavedChangesModal.askAndExecuteCallbacks(
                    async () => {
                        if (await this.saveCurrentTab()) {
                            actionIfTrue()
                        }
                    },
                    actionIfTrue)
            } else {
                actionIfTrue()
            }
        },
        async checkIfDataHasBeenChangedWhenChangedLaboratoryAndGoToRoute(goToRoute) {
            router.currentRoute.meta.skipChecking = true
            this.closePreviousCasesWindow()
            if (this.creatingSubsampleMode) {
                await HelperUtils.runActionsWhenChangeLaboratory(goToRoute)
                return
            }
            const activeTabContent = this.getActiveTabContent()
            if (activeTabContent?.hasBeenChanged()) {
                this.$refs.unsavedChangesModal.askAndExecuteCallbacks(
                    this.saveCurrentTab,
                    () => HelperUtils.runActionsWhenChangeLaboratory(goToRoute),
                    () => { router.currentRoute.meta.skipChecking = false })
            } else {
                await HelperUtils.runActionsWhenChangeLaboratory(goToRoute)
            }
        },
        getActiveTabContent() {
            if (this.activeTabIndex === SAMPLE_FORM_TAB_INDEX) {
                return this.$refs.sampleForm
            } else if (this.activeTabIndex === MACRO_TAB_INDEX) {
                return this.$refs.macroEditor
            } else if (this.activeTabIndex === PORTION_TAB_INDEX) {
                return this.$refs.portionGrid
            } else if (this.activeTabIndex === MEDIA_TAB_INDEX) {
                return this.$refs.grossingTabPhoto
            }
            return null
        },
        requestSubsampleMode() {
            this.checkChangesInActiveTabAndApplyAction(this.activateSubsampleMode)
        },
        activateSubsampleMode() {
            this.creatingSubsampleMode = true
            this.$refs.sampleForm.addSubsample()
        },
        async loadAttachmentTypeIdForMacroAudio() {
            const response =
                await PromiseUtils.toCallSecure(
                    attachmentTypeService.getAttachmentType('SAMPLE', 'attachment.type.macroexaminationaudio.code'))
            if (response !== null) {
                this.attachmentTypeIdForMacroAudio = response.atyId
            }
        },
        async loadSample() {
            const response =
                await PromiseUtils.toCallSecure(
                    sampleProcessingSampleService.findById(this.laboratoryIdLogged, this.casId, this.samId))
            if (response !== null) {
                this.sample = response
            }
        },
        isSubsample() {
            return this.sample?.parentSampleSamId
        },
        canBeDeleted() {
            return this.isSubsample() &&
                [SAMPLE_STATES.DRAFT.code, SAMPLE_STATES.REGISTERED.code].includes(this.sample.status.code)
        },
        replaceClickOnTabEvents() {
            const tabs = document.getElementsByClassName('manuallyControlledTab')
            if (tabs.length === 0) {
                setTimeout(this.replaceClickOnTabEvents, 100)
                return
            }
            for (let i = 0; i < tabs.length; i++) {
                const tab = tabs.item(i)
                tab.addEventListener('click', this.navigateToTab)
            }
        },
        navigateToTab(mouseEvent) {
            const newTabIndex = Number.parseInt(mouseEvent.currentTarget.children[0].getAttribute('aria-posinset')) - 1
            this.checkChangesInActiveTabAndApplyAction(() => { this.activeTabIndex = newTabIndex })
        },
        shortKeyPressed(tabIndex) {
            this.checkChangesInActiveTabAndApplyAction(() => { this.activeTabIndex = tabIndex })
        },
        async navigateToSample(casId, samId) {
            await router.push({
                name: this.routeIsGrossing ? 'grossingSampleView' : 'laboratorySampleView',
                params: {
                    casId: casId,
                    samId: samId
                }
            })
        },
        async loadData() {
            this.casId = parseInt(this.$route.params.casId)
            this.samId = parseInt(this.$route.params.samId)
            await this.reloadCase()
            await Promise.all([
                this.$refs.samplesCards?.loadSamples(),
                this.getSectionsSummary(),
                this.loadSample(),
                this.loadAttachmentTypeIdForMacroAudio()
            ])
            if (this.$route.params.porId) {
                this.checkChangesInActiveTabAndApplyAction(() => { this.activeTabIndex = PORTION_TAB_INDEX })
            }
            this.replaceClickOnTabEvents()
        },
        async askAndGetAnswer(question) {
            return new Promise((resolve) => {
                this.message = question
                this.$refs.confirmationModal.ask(
                    async () => resolve(true),
                    async () => resolve(false))
            })
        },
        async deleteSubsample() {
            const shouldDeleteSubsample = await this.askAndGetAnswer(this.$tc(getSampleDeletionConfirmationMessage([this.sample])))
            if (shouldDeleteSubsample) {
                const [error] = await this.toCall(sampleProcessingSampleService.deleteSample(this.laboratoryIdLogged, this.casId, this.samId))
                if (error) {
                    const errorMessage = error.response && error.response.data ? error.response.data.message : ''
                    this.showErrorModalGlobal(this.$tc('grossing.error.deleteSubsample') + `: ${errorMessage}`)
                    return
                }
                await this.navigateToSample(this.casId, this.sample.parentSampleSamId)
            }
        },
        visibleHasUnauthorizedBillableConceptsIcon(hasUnauthorizedBillableConcepts) {
            return ModuleOperationGuard.canAccessBilling && hasUnauthorizedBillableConcepts
        }
    }
}
</script>

<style lang="scss">
    .grossingSample {
        &.page__main-content {
            padding-bottom: 40px;

            .mainLayout {
                flex-grow: 1;
                height: 100%;
                display: flex;
                flex-direction: column;

                .mainLayout__grossing-area {
                    height: 100%;
                    flex-grow: 1;
                    display: flex;
                    flex-direction: column;

                    .customHeight {
                        flex-grow: 1;
                        min-height: 200px;

                        .mainCard {
                            height: 100%;
                            display: flex;
                            flex-direction: column;

                            .grossing-container {
                                height: 100%;

                                .cardsColumn {
                                    display: flex;
                                    flex-direction: column;

                                    .sample-cards-scrolling-area {
                                        flex-grow: 1;
                                        overflow-y: auto;
                                    }

                                    .cards-footer {
                                        z-index: 1;
                                    }
                                }

                                .tabsColumn {

                                    .tab-content {
                                        height: 100%;

                                        .tab-pane {
                                            height: 100%;
                                            display: flex;
                                            flex-direction: column;

                                            .section-content {
                                                flex-grow: 1;

                                                &.description-content {
                                                    display: flex;

                                                    .descriptionEditor {
                                                        display: flex;
                                                        flex-direction: column;
                                                        width: 100%;

                                                        .diagnosis-layout-height {
                                                            flex-grow: 1;
                                                            min-height: 0;

                                                            .col {
                                                                min-height: 0;
                                                                height: 100%;

                                                                .quill-editor {
                                                                    height: 100%;
                                                                    display: flex;
                                                                    flex-direction: column;

                                                                    .ql-container {
                                                                        flex-grow: 1;
                                                                        margin-bottom: 0.75rem;
                                                                        min-height: 0;
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }

                                        }
                                    }
                                }

                                .tabsRow {
                                    height:calc(100% - 35px);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
.grossingSample .no-grossing-empty-state svg {
    width: 150px;
}

.grossingSample .no-grossing-empty-state svg .st2 {
    fill: vt-color(grey, .5);
}

.grossingSample .no-grossing-empty-state .row .col:first-child {
    display: flex;
    align-items: center;
    justify-content: center;
}

.grossingSample .no-grossing-empty-state .no-grossing-empty-box {
    width: calc(100% - 15px);
    height: calc(100% - 40px);
    background: vt-color(light-grey-alt, .8);
    border-radius:5px;
}

.grossingSample .no-grossing-empty-state .circle {
    position: absolute;
    width: 150px;
    height: 150px;
    background: vt-color(primary);
    opacity: .2;
    border-radius: 50%;
}

.grossingSample .btn-badge {
    @include badgeCounter();
}

.grossingSample .section-bubble {
  width: 6px;
  height: 6px;
  background: #f5b372;
  border-radius: 50%;
  position: absolute;
  right: 7px;
  margin-top: 4px;
}

.grossingSample .btn-badge .vt-nav-badge {
  left: 0;
  bottom: 0;
}

.grossingSample .card-truncated {
    @include mq(desktop-wide, max) {
        @include text-truncate(160px);
    }
    @include mq(desktop-wide, min) {
        @include text-truncate();
    }
    @include mq(desktop, max) {
        @include text-truncate(100px);
    }
    @include mq(tablet-wide, max) {
        @include text-truncate(120px);
    }
  height: 15px;
}

.grossingSample .showtooltip {
  display: block !important;
  cursor: pointer;
}

.grossingSample .noshowtooltip {
  display: none !important;
}

.grossingSample .no-gutters > .col,
.no-gutters > [class*="col-"] {
  height: 100%;
}

.grossingSample .section-content {
    height: calc(100vh - 278px) !important;
    overflow-y: auto;
    overflow-x: hidden;
}

.grossingSample .sample-processing-macro-tab .section-content {
    overflow: visible;
    height: 100px !important;
}

.grossingSample .tabs.row {
  margin-top: 12px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  height: calc(100% - 22px);
  position: relative;
}

.grossingSample .section-content .title {
  color: vt-color(primary);
  font-size: font-size(big);
}

.grossingSample .tabs.row {
  border: 1px solid vt-color(card-border);
}

.grossing-footer {
  border-top: 1px solid vt-color(card-border);
  position: absolute;
  width: 100%;
  bottom: 0;
  height: 35px;
}

.grossingSample .flex-btn {
  width: 30% !important;
}

.grossingSample .cardsColumn,
.grossingSample .cardsCompact {
  overflow: hidden;
  overflow-y: auto;
}

.grossingSample .cardsColumn .sample-cards-scrolling-area {
  height: calc(100vh - 285px);
  overflow-x: hidden;
  overflow-y: auto;
}

.grossingSample .tab-pane .section-content {
  padding: 0 1.25rem;
}

.grossingSample .tabsColumn {
  border-right: 1px solid vt-color(card-border);
}

.grossingSample .scrolling-area {
  height: calc(80vh - 80px);
  overflow: hidden;
  overflow-y: auto;
}

.grossingSample .grossing-container {
  padding-left: 1rem;
  padding-right: 1rem;
}

.grossingSample .line-tabs .nav-tabs {
  width: 100% !important;
}

.grossingSample .tab-btn {
  position: absolute;
  top: 4px;
  right: 1rem;
}

.grossingSample .nav-item a {
  padding: 0.4rem 1.5rem !important;
}

.grossingSample .mainCard .card-body {
  padding: 0;
  height: 100%;
}

.grossingSample .registry-request-grid {
  height: calc(100vh - 420px) !important;
}

.grossingSample .registry-photo-grid {
  height: calc(100vh - 452px) !important;
}

.grossingSample .entity-columns {
  flex: 0 0 45% !important;
}

@media (max-width: 1370px) {

  .grossingSample .sample-cards-scrolling-area {
    height: calc(100vh - 285px);
    overflow-y: auto;
    overflow-x: hidden;
  }

  .grossingSample .section-content .title {
    font-size: font-size(normal);
    color: vt-color(primary);
  }

  .grossingSample .title-container {
    flex: 0 0 15rem !important;
  }

  .grossingSample .line-tabs .nav-tabs {
    width: 100% !important;
  }

  .grossingSample .registry-request-grid {
    height: calc(100vh - 420px) !important;
  }

  .grossingSample .registry-photo-grid {
    height: calc(100vh - 342px) !important;
  }
}

.grossingSample .manuallyControlledTab {
  cursor: pointer;
}

.grossingSample .manuallyControlledTab > a {
  pointer-events: none;
}
.grossingSample .vt-search-bar input {
    @include mq(tablet-medium, max) {
        width: 110px;
    }
}
.simple-pop-inside-reg {
    .btn {
        padding: 0;
        font-size: .875rem;
        word-wrap: break-word;
    }
}
</style>

<style lang="scss" scoped>

.gr-card__patient-fullname {
    @include mq(tablet-medium, max) {
        @include text-truncate(160px);
    }
}

.sample-btns {
  border-bottom: 1px solid vt-color(card-border);
}

.sample-btns .btn__icon .btn__icon--primary {
  margin-right: 0 !important;
}

.grossingSample .nav-link.active .section-icon {
  opacity: 1;
}

.grossingSample .section-icon {
  color: vt-color(primary);
  font-size: font-size(big);
  transition: 0.2s;
}

.cards-footer {
  position: absolute;
  bottom: 5px;
  right: 3px;
  background: vt-color(card-bg);
  border-radius: 50%;
  width: 25px;
  height: 25px;
}

.cards-footer-contract {
  position: absolute;
  bottom: 0;
  right: 0;
  background: vt-color(card-bg);
  width: 100%;
  height: 35px;
  border-radius: 0 0 0 0.25rem;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  transition: all 0.3s;
}

.switch::after {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 18px;
  background-color: white;
  top: 1px;
  left: 1px;
  transition: all 0.3s;
}

input[type="checkbox"]:checked + .switch::after {
  transform: translateX(20px);
}

input[type="checkbox"]:checked + .switch {
  background-color: vt-color(primary);
}

.offscreen {
  position: absolute;
  left: -9999px;
}

.hide-column {
  display: none;
}

.grossingSample .case-buttons {
  width: 30px;
  position: relative;
  display: inline-block;
}

.grossingSample .customHeight .mainCard {
  height: 100%;
}

.grossingSample .cardsColumn {
  border-right: 1px solid #d8d8d8;
}

.grossingSample .cardsCompact {
  border-right: 1px solid #d8d8d8;
}

.edition-icon {
  font-size: 10px;
  position: absolute;
  bottom: 7px;
}

@media (max-width: 1370px) {

  .grossingSample .scrolling-area {
    height: calc(80vh - 80px);
    overflow: hidden;
    overflow-y: auto;
  }
}
</style>
